/**
 * Shared POS-style order builder primitives.
 *
 * Two components:
 *   - <POSProductGrid />: searchable catalog (grid/list views) with tap-to-add.
 *   - <POSCart />: editable cart with +1/+5/+10 quick buttons + inline qty &
 *     price editing per line.
 *
 * Both are *controlled* (state lives in parent) so promotion/bonificación
 * recalculation stays as a parent concern. Used by:
 *   - SellerOrderTake.jsx  (mobile-first seller POS)
 *   - Orders.jsx          (admin "Nuevo Pedido" dialog)
 */
import { useMemo, useState } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { formatMoney } from "../../lib/money";
import { Grid3X3, List, Search, Plus, Minus, Trash2 } from "lucide-react";

/* ------------------------------------------------------------------ Grid */

export function POSProductGrid({
  products,
  lineas,
  onAddProduct,
  maxItems = 60,
  testid = "pos-product-grid",
  emptyHint = "Sin resultados",
}) {
  const [view, setView] = useState("grid"); // grid | list
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? products.filter(
          (p) =>
            (p.codigo || "").toLowerCase().includes(q) ||
            (p.descripcion || "").toLowerCase().includes(q),
        )
      : products;
    return list.slice(0, maxItems);
  }, [products, search, maxItems]);

  const inCartFor = (p) =>
    lineas.find(
      (l) =>
        (l.codigo || "").toLowerCase() === (p.codigo || "").toLowerCase() &&
        !l.es_bonificacion,
    );

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <label className="block text-sm font-medium">Catálogo</label>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant={view === "grid" ? "default" : "outline"}
            className="h-7 px-2 rounded-sm"
            onClick={() => setView("grid")}
            data-testid={`${testid}-view-grid`}
            title="Vista en cuadrícula"
          >
            <Grid3X3 className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={view === "list" ? "default" : "outline"}
            className="h-7 px-2 rounded-sm"
            onClick={() => setView("list")}
            data-testid={`${testid}-view-list`}
            title="Vista en lista"
          >
            <List className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por código o nombre…"
          className="rounded-sm pl-8"
          data-testid={`${testid}-search`}
        />
      </div>
      {view === "grid" ? (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto p-0.5"
          data-testid={`${testid}-grid`}
        >
          {filtered.length === 0 && (
            <p className="col-span-full text-center text-xs text-slate-500 py-6">
              {emptyHint}
            </p>
          )}
          {filtered.map((p) => {
            const inCart = inCartFor(p);
            return (
              <button
                key={p.id || p.codigo}
                type="button"
                onClick={() => onAddProduct(p, 1)}
                className={`relative text-left p-2 border rounded-sm transition-all hover:shadow-sm active:scale-95 ${
                  inCart
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-slate-200 bg-white hover:border-[#0B1F3A]"
                }`}
                data-testid={`${testid}-card-${p.codigo}`}
                title="Tocá para agregar al pedido"
              >
                <div className="mono text-[10px] uppercase tracking-wide font-bold text-slate-500 truncate">
                  {p.codigo}
                </div>
                <div className="text-sm font-semibold text-slate-900 truncate min-h-[20px]">
                  {p.descripcion}
                </div>
                <div
                  className="text-sm font-black mt-1"
                  style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                  {formatMoney(p.precio_venta)}
                </div>
                {inCart && (
                  <span
                    className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-black rounded-full bg-emerald-600 text-white"
                    data-testid={`${testid}-card-qty-${p.codigo}`}
                  >
                    {inCart.cantidad}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div
          className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[300px] overflow-y-auto"
          data-testid={`${testid}-list`}
        >
          {filtered.length === 0 && (
            <p className="text-center text-xs text-slate-500 py-6">{emptyHint}</p>
          )}
          {filtered.map((p) => {
            const inCart = inCartFor(p);
            return (
              <button
                key={p.id || p.codigo}
                type="button"
                onClick={() => onAddProduct(p, 1)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50 ${
                  inCart ? "bg-emerald-50/40" : ""
                }`}
                data-testid={`${testid}-row-${p.codigo}`}
              >
                <span className="mono text-xs font-medium w-20 truncate">
                  {p.codigo}
                </span>
                <span className="text-sm flex-1 truncate text-slate-700">
                  {p.descripcion}
                </span>
                <span
                  className="text-sm font-black"
                  style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
                >
                  {formatMoney(p.precio_venta)}
                </span>
                {inCart && (
                  <span className="inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 text-[10px] font-black rounded-full bg-emerald-600 text-white">
                    {inCart.cantidad}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Cart */

/**
 * Renders the editable cart. The parent owns the state and decides how to
 * re-apply promotions when qty/price changes (e.g. seller path rebuilds
 * bonus lines via `buildPromoLines`).
 *
 * Props:
 *   - lineas: OrderLine[]
 *   - onChangeQty(idx, newQty)
 *   - onChangePrice(idx, newPrice)  — optional; renders only if provided
 *   - onRemove(idx)
 *   - testid prefix
 */
export function POSCart({
  lineas,
  onChangeQty,
  onChangePrice,
  onRemove,
  testid = "pos-cart",
}) {
  const total = useMemo(
    () => lineas.reduce((s, l) => s + (l.subtotal || 0), 0),
    [lineas],
  );

  if (lineas.length === 0) {
    return (
      <div
        className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-xs text-slate-500"
        data-testid={`${testid}-empty`}
      >
        Tocá un producto del catálogo para empezar
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 overflow-hidden">
        {lineas.map((l, i) => (
          <POSCartLine
            key={`${l.codigo}-${i}`}
            line={l}
            idx={i}
            onChangeQty={onChangeQty}
            onChangePrice={onChangePrice}
            onRemove={onRemove}
            testid={`${testid}-line-${i}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
          {lineas.length} {lineas.length === 1 ? "línea" : "líneas"}
        </span>
        <span
          className="text-2xl font-black"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          data-testid={`${testid}-total`}
        >
          {formatMoney(total)}
        </span>
      </div>
    </div>
  );
}

function POSCartLine({ line, idx, onChangeQty, onChangePrice, onRemove, testid }) {
  const isBonus = !!line.es_bonificacion;
  const bumpBy = (n) => onChangeQty(idx, Math.max(1, (line.cantidad || 1) + n));
  return (
    <div
      className={`px-2 py-2 ${isBonus ? "bg-emerald-50/40" : ""}`}
      data-testid={testid}
    >
      {/* Row 1: identity + remove */}
      <div className="flex items-center gap-2">
        <span className="mono text-xs font-medium w-20 truncate text-slate-600">
          {line.codigo}
        </span>
        <span className="text-sm flex-1 truncate min-w-0 flex items-center gap-1.5">
          <span
            className={`truncate ${
              isBonus ? "text-emerald-700 font-medium" : "text-slate-700"
            }`}
          >
            {line.producto}
          </span>
          {isBonus && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200 flex-shrink-0">
              GRATIS
            </span>
          )}
        </span>
        <span className="text-sm font-bold w-20 text-right mono flex-shrink-0">
          {formatMoney(line.subtotal)}
        </span>
        <button
          type="button"
          onClick={() => onRemove(idx)}
          className="text-slate-400 hover:text-red-600 p-1"
          data-testid={`${testid}-remove`}
          title="Quitar"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Row 2: quantity + quick buttons + optional price edit */}
      {!isBonus && (
        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={() => onChangeQty(idx, Math.max(1, (line.cantidad || 1) - 1))}
            className="h-7 w-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 disabled:opacity-30"
            disabled={line.cantidad <= 1}
            data-testid={`${testid}-dec`}
            aria-label="Disminuir cantidad"
          >
            <Minus className="h-3 w-3" />
          </button>
          <input
            type="number"
            min="1"
            value={line.cantidad}
            onChange={(e) => {
              const n = Math.max(1, parseInt(e.target.value, 10) || 1);
              onChangeQty(idx, n);
            }}
            className="h-7 w-14 text-center text-xs rounded-sm border border-slate-200 focus:outline-none focus:ring-1 focus:ring-[#002FA7] mono"
            data-testid={`${testid}-qty`}
          />
          <button
            type="button"
            onClick={() => onChangeQty(idx, Math.max(1, (line.cantidad || 1) + 1))}
            className="h-7 w-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100"
            data-testid={`${testid}-inc`}
            aria-label="Aumentar cantidad"
          >
            <Plus className="h-3 w-3" />
          </button>
          {/* Quick add */}
          {[1, 5, 10].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => bumpBy(n)}
              className="h-7 px-1.5 text-[11px] font-bold rounded-sm border border-slate-200 text-slate-600 hover:bg-[#EEF2FF] hover:text-[#002FA7] hover:border-[#002FA7]"
              data-testid={`${testid}-plus-${n}`}
              title={`Sumar ${n} a la cantidad`}
            >
              +{n}
            </button>
          ))}
          {onChangePrice && (
            <label className="flex items-center gap-1 ml-auto">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                Precio
              </span>
              <input
                type="number"
                min="0"
                step="any"
                value={line.precio_unitario}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  onChangePrice(idx, isNaN(v) ? 0 : v);
                }}
                className="h-7 w-20 text-right text-xs rounded-sm border border-slate-200 focus:outline-none focus:ring-1 focus:ring-[#002FA7] mono"
                data-testid={`${testid}-price`}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}
